import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

export const detectAs400ConfirmationsConfigSchema = z.object({
  maxAgeDays: z
    .number()
    .int()
    .min(1)
    .max(365)
    .default(3)
    .describe(
      "Only check submissions sent within this many days. Sweeps run several " +
        "times a day, so anything unconfirmed after a few days is stale — the " +
        "submissions page / detail modal still checks older ones on open.",
    ),
});

export type DetectAs400ConfirmationsConfig = z.infer<
  typeof detectAs400ConfirmationsConfigSchema
>;

/**
 * Detects AS400 pickup of sent EDI 844 batch files.
 *
 * AS400 picks up GEN844 batch files from Azure Blob Storage daily (2 PM and
 * 6 PM Eastern) and signals the pickup by renaming the file — moving it into
 * the division's `processed/` subfolder with an `@@` prefix. This step probes
 * Azure for those renamed blobs and transitions any confirmed submissions
 * `sent` → `as400_confirmed`, logging an AS400_CONFIRMED activity on each
 * submission's timeline.
 */
export const detectAs400ConfirmationsStep = defineStepConfig({
  type: "detect_as400_confirmations",
  name: "Detect AS400 844 Pickups",
  description:
    "Checks Azure Blob Storage for renamed GEN844 files (AS400 pickup) and " +
    "transitions sent EDI 844 submissions to AS400-confirmed with a timeline " +
    "activity.",
  category: "Integration",
  libraryModule: "ShipAndDebit",
  inputSchema: z.object({}),
  outputSchema: z.object({
    checkedCount: z
      .number()
      .describe("Number of sent EDI 844 submissions checked"),
    confirmedCount: z
      .number()
      .describe("Number of submissions confirmed as picked up by AS400"),
  }),
  configSchema: detectAs400ConfirmationsConfigSchema,
  emits: [] as const,
  execution: {
    startToCloseTimeout: "10 minutes",
    retry: { maximumAttempts: 2 },
  },
});
